<template>
  <v-dialog :value="state.designer.open" fullscreen hide-overlay transition="dialog-bottom-transition" @input="close">
    <v-card v-if="p" class="d-flex flex-column" style="height: 100vh">
      <v-toolbar dense flat color="primary" dark style="flex: 0 0 auto">
        <v-icon left>mdi-printer-outline</v-icon>
        <v-toolbar-title>Print designer - {{ p.name }}</v-toolbar-title>
        <v-spacer />
        <span class="text-caption mr-4">{{ pageInfo }}</span>
        <v-btn text small :loading="state.sample.loading" @click="reloadData"><v-icon left small>mdi-refresh</v-icon>Refresh data</v-btn>
        <v-btn text small @click="close"><v-icon left small>mdi-check</v-icon>Done</v-btn>
      </v-toolbar>

      <div class="d-flex flex-grow-1" style="min-height: 0">
        <!-- settings -->
        <div class="pa-4" style="width: 380px; flex: 0 0 380px; overflow-y: auto; border-right: 1px solid rgba(128,128,128,.3)">
          <v-text-field v-model="p.name" outlined dense label="Layout name" />
          <v-row dense>
            <v-col cols="6"><v-select v-model="p.paper" :items="['A4', 'A5', 'Letter', 'Legal', '80mm']" outlined dense label="Paper" /></v-col>
            <v-col cols="6"><v-select v-model="p.orientation" :items="['portrait', 'landscape']" :disabled="p.paper === '80mm'" outlined dense label="Orientation" /></v-col>
          </v-row>
          <div class="text-caption">Page margin: {{ p.margin == null ? 12 : p.margin }} mm</div>
          <v-slider :value="p.margin == null ? 12 : p.margin" min="0" max="40" hide-details dense class="mb-3" @input="$set(p, 'margin', $event)" />
          <div class="text-caption">Text size: {{ p.fontSize || 11 }} px</div>
          <v-slider :value="p.fontSize || 11" min="7" max="20" hide-details dense class="mb-3" @input="$set(p, 'fontSize', $event)" />
          <v-text-field :value="p.subtitle" outlined dense label="Line under the title (optional)" @input="$set(p, 'subtitle', $event)" />

          <div class="text-subtitle-2 mt-2 mb-1">What prints</div>
          <div class="text-caption grey--text mb-2">Switch an element off and everything below it moves up. Drag report sections to change their order.</div>

          <div v-for="h in topToggles" :key="h.key" class="rb-prow d-flex align-center" :class="{ 'rb-off': header[h.key] === false }" @mouseenter="highlight(`el:${h.key}`)" @mouseleave="highlight(null)">
            <v-icon x-small class="mr-2" style="visibility: hidden">mdi-drag</v-icon>
            <span class="text-body-2 flex-grow-1">{{ h.label }}</span>
            <v-btn icon x-small :title="header[h.key] === false ? 'Show' : 'Hide'" @click="setHeader(h.key, header[h.key] === false)">
              <v-icon small>{{ header[h.key] === false ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
            </v-btn>
          </div>

          <draggable v-model="ordered" handle=".rb-ph">
            <div v-for="s in ordered" :key="s.id" class="d-flex align-center rb-prow" :class="{ 'rb-off': isHidden(s.id) }" @mouseenter="highlight(`sec:${s.id}`)" @mouseleave="highlight(null)">
              <v-icon x-small class="rb-ph mr-1" style="cursor: grab">mdi-drag</v-icon>
              <v-icon x-small class="mr-1">{{ icon(s.type) }}</v-icon>
              <span class="text-body-2 text-truncate flex-grow-1">{{ s.title || typeText(s.type) }}</span>
              <v-btn icon x-small :title="isHidden(s.id) ? 'Show' : 'Hide'" @click="toggle(s.id)">
                <v-icon small>{{ isHidden(s.id) ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
              </v-btn>
            </div>
          </draggable>

          <div class="rb-prow d-flex align-center" :class="{ 'rb-off': header.runInfo === false }" @mouseenter="highlight('el:runInfo')" @mouseleave="highlight(null)">
            <v-icon x-small class="mr-2" style="visibility: hidden">mdi-drag</v-icon>
            <span class="text-body-2 flex-grow-1">Who ran it and when</span>
            <v-btn icon x-small :title="header.runInfo === false ? 'Show' : 'Hide'" @click="setHeader('runInfo', header.runInfo === false)">
              <v-icon small>{{ header.runInfo === false ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
            </v-btn>
          </div>
          <v-text-field :value="(p.footer || {}).text" class="mt-3" @focus="highlight('el:footer')" @blur="highlight(null)" outlined dense label="Footer text (e.g. a signature line)" @input="setFooter" />
        </div>

        <!-- paper -->
        <div ref="stage" class="flex-grow-1 pa-4" style="overflow: auto; background: #cfd8dc">
          <v-alert v-if="state.sample.error" type="error" dense text>
            {{ state.sample.error }} - set the filter values in the Data tab's test values (or fix the query), then Refresh data.
          </v-alert>
          <div v-else-if="!state.sample.result" class="text-center pa-10">
            <v-progress-circular v-if="state.sample.loading" indeterminate />
            <div v-else>No data loaded yet. <v-btn small color="primary" @click="reloadData">Load data</v-btn></div>
          </div>
          <div v-else class="rb-paper-wrap" :style="wrapStyle">
            <div class="rb-paper" :style="paperStyle">
              <iframe ref="frame" :srcdoc="html" :style="frameStyle" scrolling="no" @load="measure" />
              <div class="rb-pagelines" :style="pageLines" />
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import { state, container, loadSample, SECTION_TYPES } from './editorState'
import { buildPrintHtml, paperSize, renderChartImages, printedSections } from '../rbUtil'

const MM = 3.7795 // CSS pixels per millimetre

export default {
  name: 'RbPrintDesigner',
  components: { draggable },
  data () {
    return {
      state, scale: 1, contentH: 1000, highlighted: null,
      topToggles: [{ key: 'title', label: 'Report title' }, { key: 'params', label: 'Filters used' }, { key: 'info', label: 'Calculation details' }]
    }
  },
  computed: {
    ...mapGetters(['getEndpoint']),
    c () { return container() },
    p () { return this.state.designer.open ? (this.c.prints || [])[this.state.designer.index] || null : null },
    header () { return (this.p && this.p.header) || {} },
    allSections () { return this.c.ui.sections },
    // every section in print order (switched-off ones keep their place)
    ordered: {
      get () {
        const all = this.allSections
        if (Array.isArray(this.p.hiddenSections)) {
          const known = (this.p.sections || []).map((id) => all.find((s) => s.id === id)).filter(Boolean)
          return [...known, ...all.filter((s) => !(this.p.sections || []).includes(s.id))]
        }
        const on = this.p.sections && this.p.sections.length ? this.p.sections.map((id) => all.find((s) => s.id === id)).filter(Boolean) : all
        return [...on, ...all.filter((s) => !on.includes(s))]
      },
      set (list) {
        this.materialize()
        this.$set(this.p, 'sections', list.map((s) => s.id))
      }
    },
    dims () { return paperSize(this.p) },
    ctx () {
      const t = this.state.tpl
      return {
        template: JSON.parse(JSON.stringify(t)), drill: this.state.ctx === 'main' ? null : this.state.ctx, result: this.state.sample.result,
        values: this.state.previewParams, options: this.state.paramOptions, user: 'Your name', reportKey: t.key, version: ''
      }
    },
    html () {
      if (!this.p || !this.state.sample.result) return ''
      const images = renderChartImages(printedSections(this.allSections, this.p), this.state.sample.result)
      return buildPrintHtml(this.ctx, this.p, images, { preview: true })
    },
    pxW () { return this.dims[0] * MM },
    pxH () { return this.dims[1] * MM },
    wrapStyle () { return { width: `${this.pxW * this.scale}px`, height: `${Math.max(this.contentH, this.pxH) * this.scale}px`, margin: '0 auto' } },
    paperStyle () { return { width: `${this.pxW}px`, height: `${Math.max(this.contentH, this.pxH)}px`, transform: `scale(${this.scale})`, transformOrigin: 'top left' } },
    frameStyle () { return { width: `${this.pxW}px`, height: `${Math.max(this.contentH, this.pxH)}px`, border: '0', background: '#fff' } },
    pageLines () {
      return { backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${this.pxH - 2}px, rgba(244, 67, 54, 0.8) ${this.pxH - 2}px, rgba(244, 67, 54, 0.8) ${this.pxH}px)` }
    },
    pageInfo () {
      if (this.p && this.p.paper === '80mm') return 'Receipt roll: continuous'
      return `${this.dims[0].toFixed(0)} x ${this.dims[1].toFixed(0)} mm - about ${Math.max(1, Math.ceil(this.contentH / this.pxH))} page(s) - red lines mark page breaks`
    }
  },
  watch: {
    'state.designer.open' (open) { if (open) this.onOpen() },
    dims () { this.$nextTick(this.fit) }
  },
  mounted () { window.addEventListener('resize', this.fit) },
  beforeDestroy () { window.removeEventListener('resize', this.fit) },
  methods: {
    icon (t) { return (SECTION_TYPES.find((x) => x.value === t) || {}).icon },
    typeText (t) { return (SECTION_TYPES.find((x) => x.value === t) || {}).text },
    async onOpen () {
      if (!state.sample.result || state.sample.ctx !== state.ctx) await this.reloadData()
      this.$nextTick(this.fit)
    },
    async reloadData () { await loadSample(this.getEndpoint) ; this.$nextTick(this.fit) },
    fit () {
      const stage = this.$refs.stage
      if (stage) this.scale = Math.min(1, (stage.clientWidth - 40) / this.pxW)
    },
    // the preview iframe is as tall as the document it shows
    measure () {
      this.highlighted = null
      const f = this.$refs.frame
      try { this.contentH = f.contentDocument.documentElement.scrollHeight } catch (e) { /* cross-origin cannot happen for srcdoc */ }
      this.fit()
    },
    // outline the hovered element on the paper (and scroll it into view if it is off screen)
    highlight (key) {
      const f = this.$refs.frame
      let doc
      try { doc = f && f.contentDocument } catch (e) { return }
      if (!doc || !doc.body) return
      if (!doc.getElementById('rb-hl-style')) {
        const st = doc.createElement('style')
        st.id = 'rb-hl-style'
        st.textContent = '.rb-hl { outline: 3px solid #1976d2; outline-offset: 3px; background: rgba(25, 118, 210, 0.08) !important; }'
        doc.head.appendChild(st)
      }
      doc.querySelectorAll('.rb-hl').forEach((n) => n.classList.remove('rb-hl'))
      this.highlighted = key
      if (!key) return
      const el = doc.querySelector(`[data-rb="${key}"]`)
      if (!el) return
      el.classList.add('rb-hl')
      const stage = this.$refs.stage
      if (stage) {
        const top = el.getBoundingClientRect().top * this.scale
        const bottom = top + el.getBoundingClientRect().height * this.scale
        const wrap = stage.querySelector('.rb-paper-wrap')
        const offset = wrap ? wrap.offsetTop : 0
        if (offset + top < stage.scrollTop || offset + bottom > stage.scrollTop + stage.clientHeight) {
          stage.scrollTo({ top: Math.max(0, offset + top - 40), behavior: 'smooth' })
        }
      }
    },
    setHeader (key, on) {
      if (!this.p.header) this.$set(this.p, 'header', {})
      this.$set(this.p.header, key, on)
    },
    setFooter (text) {
      if (!this.p.footer) this.$set(this.p, 'footer', {})
      this.$set(this.p.footer, 'text', text)
    },
    // old layouts only listed what to print; convert to "full order + switched-off list" before the first change
    materialize () {
      if (Array.isArray(this.p.hiddenSections)) return
      const legacyOn = this.p.sections && this.p.sections.length ? this.p.sections : this.allSections.map((s) => s.id)
      const order = this.ordered.map((s) => s.id)
      this.$set(this.p, 'sections', order)
      this.$set(this.p, 'hiddenSections', order.filter((id) => !legacyOn.includes(id)))
    },
    isHidden (id) {
      return Array.isArray(this.p.hiddenSections) ? this.p.hiddenSections.includes(id) : !!(this.p.sections && this.p.sections.length && !this.p.sections.includes(id))
    },
    toggle (id) {
      this.materialize()
      const set = new Set(this.p.hiddenSections)
      if (set.has(id)) set.delete(id)
      else set.add(id)
      this.$set(this.p, 'hiddenSections', [...set])
    },
    close () { this.state.designer.open = false }
  }
}
</script>

<style scoped>
.rb-off { opacity: 0.45; }
.rb-off span { text-decoration: line-through; }
.rb-prow { padding: 3px 6px; border: 1px solid rgba(128, 128, 128, 0.35); border-radius: 4px; margin-bottom: 4px; background: #fff; }
.rb-paper { background: #fff; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35); position: relative; }
.rb-paper iframe { display: block; }
.rb-pagelines { position: absolute; inset: 0; pointer-events: none; }
</style>
